<template>
  <v-dialog
    v-model="isVisible"
    persistent
    scrollable
    width="800"
  >
    <v-card ref="dialogCard">
      <div ref="dialogHeader">
        <v-toolbar
          color="secondary"
          dark
          tile
        >
          <v-toolbar-title>
            {{ dialogTitle }}
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            v-if="!isLoading"
            icon
            @click="onCloseDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <div
          v-if="!isLoading && !isTwilioConfigured"
          class="px-6 py-3 secondary"
        >
          <v-alert type="warning">
            Twilio is not configured. This message will not be sent until it is configured by an Administrator.
          </v-alert>
        </div>

        <v-tabs
          v-if="!isLoading"
          v-model="selectedTab"
          active-class="active-tab"
          background-color="secondary"
          class="editor-tabs"
          dark
          grow
          hide-slider
          icons-and-text
        >
          <v-tab
            v-for="tab of enabledTabs"
            :key="tab.value"
            :disabled="tabDisabledFlag(tab.value)"
            :tab-value="tab.value"
            @click.native.prevent.stop.capture="_ => onTabClick(tab)"
          >
            {{ tab.label }}
            <v-badge
              bordered
              bottom
              :color="getTabBadgeColor(tab)"
              :offset-x="14"
              :offset-y="18"
              overlap
              :value="tab.errors || tab.warnings"
            >
              <template #badge>
                <v-icon>{{ getTabBadgeIcon(tab) }}</v-icon>
              </template>

              <v-icon large>
                {{ tab.icon }}
              </v-icon>
            </v-badge>
          </v-tab>
        </v-tabs>

        <v-divider />
      </div>

      <template v-if="isLoading">
        <div
          class="d-flex align-center justify-center"
          style="height: 350px;"
        >
          <v-progress-circular
            color="primary"
            indeterminate
            size="64"
          />
        </div>
      </template>

      <template v-else>
        <v-card-text
          ref="dialogCardContent"
          class="pa-2 ma-0"
          :style="cardContentStyle"
        >
          <v-tabs-items
            ref="tabsItems"
            v-model="selectedTab"
          >
            <v-tab-item
              value="cloning-options"
            >
              <DocketEntryEditorCloningOptions
                ref="cloning-options"
                :valid.sync="areCloningOptionsValid"
              />
            </v-tab-item>

            <v-tab-item
              :eager="true"
              value="overview"
            >
              <TextMessageEditorOverview
                ref="overview"
                :send-immediately.sync="sendImmediately"
                :style="tabStyle"
                :valid.sync="isOverviewValid"
              />
            </v-tab-item>

            <v-tab-item
              :eager="true"
              value="relations"
            >
              <DocketEntryEditorRelations
                ref="relations"
              />
            </v-tab-item>

            <v-tab-item
              value="confirmation"
            >
              <TextMessageEditorConfirmation
                ref="confirmation"
                :send-immediately="sendImmediately"
              />
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>

        <div ref="dialogFooter">
          <v-divider />

          <v-card-actions>
            <div class="w-50 d-flex">
              <v-btn
                text
                @click="_ => onCloseDialog()"
              >
                Cancel
              </v-btn>
              <v-spacer />
              <v-btn
                :disabled="isFirstTabSelected"
                text
                @click="gotoPreviousTab"
              >
                <v-icon left>
                  mdi-chevron-left
                </v-icon>
                {{ previousTabName }}
              </v-btn>
            </div>
            <div class="w-50 d-flex">
              <v-btn
                :disabled="isLastTabSelected"
                text
                @click="_ => gotoNextTab()"
              >
                {{ nextTabName }}
                <v-icon right>
                  mdi-chevron-right
                </v-icon>
              </v-btn>
              <v-spacer />
              <v-btn
                color="warning"
                :disabled="!editedDocketEntryWasChanged"
                text
                @click="onResetDocketEntry"
              >
                <v-icon left>
                  mdi-restore
                </v-icon>
                Reset
              </v-btn>
              <v-btn
                color="success"
                :disabled="!canSaveDocketEntry"
                @click="onSaveDocketEntry"
              >
                <v-icon left>
                  {{ saveActionIcon }}
                </v-icon>
                {{ saveActionText }}
              </v-btn>
            </div>
          </v-card-actions>
        </div>
      </template>

      <div
        v-if="!isLoading && isLocked"
        class="overlay-parent d-flex flex-column align-center justify-center pa-8"
      >
        <v-alert
          icon="mdi-message-lock-outline"
          prominent
          type="error"
        >
          <p>This Text Message is using a template to autogenerate the message to be sent.</p>
          <p>If you unlock it you'll be able to manually make changes to it's content but updates to the template will no longer be applied</p>
          <div class="d-flex">
            <v-btn
              color="error"
              depressed
              @click="_ => unlockTextMessage()"
            >
              <v-icon left>
                mdi-lock-open-outline
              </v-icon>
              Unlock Text Message
            </v-btn>
            <v-spacer />
            <v-btn
              :dark="false"
              depressed
              @click="_ => onCloseDialog()"
            >
              Cancel
            </v-btn>
          </div>
        </v-alert>
      </div>

      <div
        v-if="isSavingDocketEntry"
        class="overlay-parent d-flex align-center justify-center"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="128"
        />
      </div>
    </v-card>

    <ConfirmationDialog
      v-model="isConfirmSaveDialogVisible"
      title="Action Confirmation"
      cancel-button-label="Go Back"
      confirm-button-label="Save Anyway"
      @confirmed="_ => onSaveDocketEntry(true)"
    >
      <template #message>
        <p>
          You haven't opened some tabs, do you want to check out any of them before saving?
        </p>
        <div class="pa-4">
          <div
            v-for="tab of unvisitedTabs"
            :key="tab.value"
            class="d-flex justify-center pa-2"
          >
            <v-btn
              depressed
              small
              @click="_ => openTab(tab.value)"
            >
              <v-icon left>
                {{ tab.icon }}
              </v-icon>
              Go to {{ tab.label }}
            </v-btn>
          </div>
        </div>
      </template>
    </ConfirmationDialog>

    <ConfirmationDialog
      v-model="isResetDocketEntryConfirmationDialogVisible"
      title="Action Confirmation"
      message="Are you sure you want to reset all data? All your changes will be lost!"
      cancel-button-label="No"
      cancel-button-color="primary"
      confirm-button-label="Yes"
      confirm-button-color="secondary"
      invert-action-buttons
      @confirmed="resetDocketEntry"
    />

    <ConfirmationDialog
      v-model="isCloseDialogConfirmationDialogVisible"
      title="Action Confirmation"
      message="Are you sure you want to exit? All your changes will be lost!"
      cancel-button-label="Stay"
      cancel-button-color="primary"
      confirm-button-label="Exit, don't save"
      confirm-button-color="secondary"
      invert-action-buttons
      @confirmed="closeDialog"
    />
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment-timezone'

import { alertHandler } from '@/alertHandler'
import { runRequestsInSequence } from '@/utilities'

import { DOCKET_ENTRY_NAMESPACE } from '@/store/docket-entry'
import { DOCKET_ENTRY_TYPE_NAMESPACE } from '@/store/docket-entry-type'
import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { SHARED_NAMESPACE } from '@/store/shared'

import ConfirmationDialog from '@/components/Dialogs/Common/ConfirmationDialog.vue'
import DocketEntryEditorCloningOptions from '@/components/DocketEntry/Editor/DocketEntryEditorCloningOptions.vue'
import DocketEntryEditorRelations from '@/components/DocketEntry/Editor/DocketEntryEditorRelations.vue'
import TextMessageEditorConfirmation from './TextMessageEditorConfirmation.vue'
import TextMessageEditorOverview from './TextMessageEditorOverview.vue'

export default {
  name: 'TextMessageEditorDialog',

  components: {
    ConfirmationDialog,
    DocketEntryEditorCloningOptions,
    DocketEntryEditorRelations,
    TextMessageEditorConfirmation,
    TextMessageEditorOverview,
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      cardContentHeight: 350,
      isCloseDialogConfirmationDialogVisible: false,
      isConfirmSaveDialogVisible: false,
      isLoading: true,
      isResetDocketEntryConfirmationDialogVisible: false,
      isSavingDocketEntry: false,
      selectedTab: null,
      sendImmediately: false,
      tabList: [
        {
          enabled: false,
          errors: false,
          icon: 'mdi-content-duplicate',
          label: 'Cloning Options',
          value: 'cloning-options',
          visited: false,
          warnings: false,
        },
        {
          enabled: true,
          errors: false,
          icon: 'mdi-compass-outline',
          label: 'Overview',
          value: 'overview',
          visited: false,
          warnings: false,
        },
        {
          enabled: true,
          errors: false,
          icon: 'mdi-transit-connection-variant',
          label: 'Relations',
          value: 'relations',
          visited: false,
          warnings: false,
        },
        {
          enabled: true,
          errors: false,
          icon: 'mdi-cellphone-check',
          label: 'Confirmation',
          value: 'confirmation',
          visited: false,
          warnings: false,
        },
      ],
    }
  },

  computed: {
    actionDate () {
      return this.docketEntry?.action_date
    },

    areCloningOptionsValid: {
      get () { return this.getTabErrorStatus('cloning-options') },
      set (val) { this.setTabErrorStatus('cloning-options', val) },
    },

    canSaveDocketEntry () {
      const allTabsValid = !this.enabledTabs.find(et => et.errors || et.warnings)
      const allTabsVisited = !this.enabledTabs.find(et => !et.visited)
      const blocked = this.isNewDocketEntry && !allTabsVisited
      return this.editedDocketEntryWasChanged && allTabsValid && !blocked
    },

    cardContentStyle () {
      return `height: ${this.cardContentHeight}px`
    },

    dialogTitle () {
      if (this.isLoading) return 'Loading, please wait ..'

      if (!this.docketEntryEditorDocketEntryId) {
        return 'Send a Text Message'
      } else if (this.isDocketEntryEditorInCloneMode) {
        return 'Clone Text Message'
      }

      return 'Edit Short Message'
    },

    docketEntry () {
      return this.editedDocketEntry
    },

    enabledTabs () {
      return this.tabList.filter(tab => tab.enabled)
    },

    hasUnvisitedTabs () {
      return !!this.unvisitedTabs.length
    },

    isFirstTabSelected () {
      return this.selectedTab === this.enabledTabs[0].value
    },

    isLastTabSelected () {
      return this.selectedTab === this.enabledTabs[this.enabledTabs.length - 1].value
    },

    isLocked () {
      return this.docketEntry?.is_locked
    },

    isNewDocketEntry () {
      return !this.docketEntry.id
    },

    isOverviewValid: {
      get () { return !this.getTabErrorStatus('overview') },
      set (val) { this.setTabErrorStatus('overview', !val) },
    },

    isTwilioConfigured () {
      return this.features?.twilio?.enabled
    },

    isVisible: {
      get () {
        return this.isTextMessageEditorVisible
      },

      set (val) {
        if (val) return
        this.closeTextMessageEditor()
      },
    },

    legalCase () {
      return this.legalCases?.find(lc => lc.id === this.docketEntry?.legal_case_id)
    },

    nextTabName () {
      const idx = this.enabledTabs.findIndex(et => et.value === this.selectedTab)

      if (idx < this.enabledTabs.length - 1) return this.enabledTabs[idx + 1].label
      return 'Next'
    },

    previousTabName () {
      const idx = this.enabledTabs.findIndex(et => et.value === this.selectedTab)

      if (idx > 0) return this.enabledTabs[idx - 1].label
      return 'Back'
    },

    saveActionIcon () {
      return this.sendImmediately ? 'mdi-message-check-outline' : 'mdi-content-save'
    },

    saveActionText () {
      return this.sendImmediately ? 'Send' : 'Save'
    },

    selectedDocketEntryType () {
      return this.docketEntryTypes.find(det => det.name === 'Text Message')
    },

    tabStyle () {
      return `min-height: ${this.cardContentHeight - 16}px`
    },

    unvisitedTabs () {
      return this.enabledTabs.filter(et => !et.visited)
    },

    ...mapGetters(DOCKET_ENTRY_TYPE_NAMESPACE, [
      'docketEntryTypes',
    ]),

    ...mapGetters(DOCKET_ENTRY_NAMESPACE, [
      'docketEntryEditorDocketEntryId',
      'editedDocketEntry',
      'editedDocketEntryWasChanged',
      'isDocketEntryEditorInCloneMode',
      'isDocketEntryEditorInRescheduleMode',
      'isTextMessageEditorVisible',
    ]),

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'legalCases',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'features',
    ]),
  },

  watch: {
    actionDate () {
      this.calculateTabsVisibility()
    },

    isExpanded () {
      this.calculateCardContentHeight()
    },

    isLoading () {
      this.$nextTick().then(_ => this.calculateCardContentHeight())
    },

    isTextMessageEditorVisible: {
      handler (value) {
        if (!value) return
        this.onEditorIsVisible()
      },

      immediate: true,
    },

    selectedDocketEntryType () {
      this.calculateTabsVisibility()
    },

    selectedTab: {
      handler (newVal, oldVal) {
        const tab = this.tabList.find(tl => tl.value === newVal)
        if (tab) tab.visited = true

        this.validateTab(oldVal)

        const newTab = this.$refs[newVal]
        if (newTab?.onTabOpened) newTab.onTabOpened()
      },

      immediate: true,
    },
  },

  methods: {
    calculateCardContentHeight () {
      if (!this.$refs.dialogCardContent) {
        this.cardContentHeight = 350
        return
      }

      const dialogHeaderHeight = this.$refs.dialogHeader.offsetHeight
      const dialogFooterHeight = this.$refs.dialogFooter?.offsetHeight || 0

      const reservedHeight = dialogHeaderHeight + dialogFooterHeight + 48
      const browserViewHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0) * 0.9

      this.cardContentHeight = Math.floor((browserViewHeight - reservedHeight) * (this.isExpanded ? 1 : 0.75))
    },

    calculateTabsVisibility () {
      const cloningOptionsTab = this.tabList.find(tab => tab.value === 'cloning-options')
      cloningOptionsTab.enabled = this.isDocketEntryEditorInCloneMode

      const selectedTab = this.tabList.find(tab => tab.value === this.selectedTab)
      if (!selectedTab?.enabled) this.selectedTab = 'overview'
    },

    closeDialog () {
      this.closeTextMessageEditor()
    },

    getTabBadgeColor (tab) {
      if (tab.errors) return 'error'
      if (tab.warnings) return 'warning'
    },

    getTabBadgeIcon (tab) {
      if (tab.errors) return 'mdi-close-thick'
      if (tab.warnings) return 'mdi-exclamation-thick'
    },

    getTabClasses (tab) {
      if (tab.value === this.selectedTab) return 'primary'
      if (tab.errors) return 'tab-error'
      if (tab.warnings) return 'tab-warning'
      return 'transparent'
    },

    getTabErrorStatus (tab) {
      return this.tabList.find(t => t.value === tab).errors
    },

    getTabIcon (tab) {
      if (tab.errors) return 'mdi-close-octagon-outline'
      if (tab.warnings) return 'mdi-alert-outline'
      return tab.icon
    },

    getTabWarningStatus (tab) {
      return this.tabList.find(t => t.value === tab).warnings
    },

    gotoNextTab () {
      if (this.isNewDocketEntry && !this.validateTab(this.selectedTab)) return
      const idx = this.enabledTabs.findIndex(et => et.value === this.selectedTab)
      if (idx < this.enabledTabs.length - 1) this.selectedTab = this.enabledTabs[idx + 1].value
    },

    gotoPreviousTab () {
      const idx = this.enabledTabs.findIndex(et => et.value === this.selectedTab)

      if (this.isNewDocketEntry && (this.enabledTabs[idx].errors || this.enabledTabs[idx].warnings)) return

      if (idx > 0) {
        this.selectedTab = this.enabledTabs[idx - 1].value
      }
    },

    onCloseDialog () {
      if (this.editedDocketEntryWasChanged) {
        this.isCloseDialogConfirmationDialogVisible = true
      } else {
        this.closeDialog()
      }
    },

    async onEditorIsVisible () {
      this.isLoading = true

      if (this.isDocketEntryEditorInCloneMode) this.selectedTab = 'cloning-options'

      await this.getFeatures()
      await this.getUsersModel()

      await this.prepareDocketEntryData()
      this.calculateTabsVisibility()
    },

    onResetDocketEntry () {
      if (this.editedDocketEntryWasChanged) this.isResetDocketEntryConfirmationDialogVisible = true
    },

    onSaveDocketEntry (confirmed = false) {
      if (this.isNewDocketEntry && this.hasUnvisitedTabs && !confirmed) {
        this.isConfirmSaveDialogVisible = true
      } else {
        this.isSavingDocketEntry = true
        this.saveDocketEntry()
      }
    },

    onTabClick (newTab) {
      if (newTab.value === this.selectedTab) return
      if (this.isNewDocketEntry && !this.validateTab(this.selectedTab)) return
      this.selectedTab = newTab.value
    },

    openTab (tab) {
      this.selectedTab = tab
    },

    async prepareDocketEntryData () {
      this.isLoading = true

      if (this.docketEntryEditorDocketEntryId) {
        if (this.isDocketEntryEditorInCloneMode || this.isDocketEntryEditorInRescheduleMode) {
          await this.getDocketEntryForCloning(this.docketEntryEditorDocketEntryId)
        } else {
          await this.getDocketEntryForEdition(this.docketEntryEditorDocketEntryId)
        }
      } else {
        const result = await this.getOptions({ field: 'site_timezone', model: 'SiteSetting::Timezone' })
        const timezone = result[0]?.timezone

        await this.resetEditedDocketEntryData({ timezone: timezone })
      }

      if (!this.legalCases.find(lc => lc.id === this.docketEntry.legal_case_id)) {
        await this.fetchLegalCase(this.docketEntry.legal_case_id)
      }

      await this.getDocketEntryTypes({
        action_type: 'TextMessage',
        archived: false,
        legal_case_type_ids: [this.legalCase.legal_case_type_id],
      })

      await this.loadDocketEntriesInfo({ legalCaseId: this.docketEntry.legal_case_id })

      this.isLoading = false
    },

    resetDocketEntry () {
      this.resetEditedDocketEntryWithOriginalData().
        catch(error => this.displayAlert('error', error)).
        finally(_ => {
          this.isResetDocketEntryConfirmationDialogVisible = false

          if (this.isNewDocketEntry) {
            this.tabList.forEach(tab => { tab.visited = false })
            const firstTab = this.tabList.find(tab => tab.enabled)
            this.selectedTab = firstTab.value
          }
        })
    },

    saveDocketEntry (filesToUpload) {
      if (!this.docketEntry.action_date || !this.docketEntry.event_time_start) {
        const now = moment.tz(this.docketEntry.timezone)
        this.docketEntry.action_date = now.format('YYYY-MM-DD')
        this.docketEntry.event_time_start = now.format('hh:mm A')
      }

      this.saveEditedDocketEntry().
        then(_ => this.uploadFiles(filesToUpload)).
        then(_ => {
          this.setCreatedDocketEntryId(this.docketEntry.id)
          this.displayAlert('success', `${this.docketEntry.action_type} saved successfully.`)
          this.closeDialog()
        }).
        catch(error => this.displayAlert('error', null, error)).
        finally(_ => {
          this.isConfirmSaveDialogVisible = false
          this.isSavingDocketEntry = false
        })
    },

    setTabErrorStatus (tab, status) {
      this.tabList.find(t => t.value === tab).errors = status
    },

    setTabWarningStatus (tab, status) {
      this.tabList.find(t => t.value === tab).warnings = status
    },

    tabDisabledFlag (tabValue) {
      if (!this.isNewDocketEntry) return false

      const tabIdx = this.enabledTabs.findIndex(et => et.value === tabValue)
      if (this.enabledTabs[tabIdx].visited) return false

      const selectedTabIdx = this.enabledTabs.findIndex(et => et.value === this.selectedTab)
      return tabIdx - 1 > selectedTabIdx
    },

    toggleDialogSize () {
      this.isExpanded = !this.isExpanded
    },

    unlockTextMessage () {
      this.docketEntry.is_locked = false
    },

    async uploadFiles (filesToUpload) {
      const fileUploadRequests = []

      if (filesToUpload) {
        filesToUpload.forEach(file => {
          const formData = new FormData()
          formData.append('file', file.data, file.name)

          fileUploadRequests.push(_ => {
            return this.uploadFilesDocketEntryModel({ fileToUpload: formData, id: this.docketEntry.id })
          })
        })
      }

      return runRequestsInSequence(fileUploadRequests, [])
    },

    validateTab (tabValue) {
      const tab = this.enabledTabs.find(et => et.value === tabValue)
      if (!tab) return false

      const tabRef = this.$refs[tabValue]
      if (tabRef?.validate) tabRef.validate()

      return !(tab.errors || tab.warnings)
    },

    ...mapActions([
      'getUsersModel',
      'uploadFilesDocketEntryModel',
    ]),

    ...mapActions(DOCKET_ENTRY_NAMESPACE, [
      'closeTextMessageEditor',
      'getDocketEntryForCloning',
      'getDocketEntryForEdition',
      'resetEditedDocketEntryData',
      'resetEditedDocketEntryWithOriginalData',
      'saveEditedDocketEntry',
    ]),

    ...mapActions(DOCKET_ENTRY_TYPE_NAMESPACE, [
      'getDocketEntryTypes',
    ]),

    ...mapActions(LEGAL_CASE_NAMESPACE, [
      'fetchLegalCase',
      'loadDocketEntriesInfo',
      'setCreatedDocketEntryId',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getFeatures',
      'getOptions',
    ]),

  },
}
</script>

<style scoped>
.active-tab {
  background-color: white;
  color: var(--v-secondary-base) !important;
}

.editor-tabs {
  .v-tab .v-badge {
    .v-badge__badge > .v-icon {
      font-size: 12px;
      color: white;
    }

    .v-badge__badge::after {
      border-color: white;
      border-width: 1.5px;
    }
  }

  .tab-error, .tab-warning {
    margin-top: 4px;
    margin-left: 2px;
    margin-right: 2px;
    background-color: transparent;
  }

  .tab-error {
    background-color: rgba(255, 0, 0, 0.2);
    color: var(--v-error-lighten1) !important;
  }

  .tab-warning {
    background-color: rgba(255, 127, 0, 0.2);
    color: var(--v-warning-lighten1) !important;
  }

  .tab-error .v-icon,
  .tab-warning .v-icon {
    color: inherit !important;
  }
}

.overlay-parent {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(255, 255, 255, 0.8);
}
</style>
